import { computed, effect, inject } from '@angular/core';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  signalStoreFeature,
  withComputed,
  withHooks,
  withMethods,
  withProps
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AlertService } from '@seatix/shared-util-alert';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';

const COUNTDOWN_DURATION_SECONDS = 60;

export function withEmailVerification() {
  return signalStoreFeature(
    withCallStatus({
      collection: 'emailVerification',
      errorType: 'string',
    }),
    withCallStatus({
      collection: 'resendVerification',
      errorType: 'string',
    }),
    withProps(() => ({
      _countdownStorage: injectLocalStorage<number | null>(
        'email-verification-countdown',
        {
          defaultValue: null,
        },
      ),
    })),
    withComputed((store) => {
      const isCountdownActive = computed(() => {
        const endTime = store._countdownStorage();
        return endTime ? Date.now() < endTime : false;
      });

      const endTime = computed(() => {
        return store._countdownStorage();
      });

      return {
        isCountdownActive,
        endTime,
      };
    }),
    withMethods((store) => {
      const authApiService = inject(AuthApiService);
      const alertService = inject(AlertService);

      function startCountdown() {
        const endTime = Date.now() + COUNTDOWN_DURATION_SECONDS * 1000;
        store._countdownStorage.set(endTime);
      }

      function clearCountdown() {
        store._countdownStorage.set(null);
      }

      return {
        startCountdown,
        clearCountdown,
        verifyEmail: rxMethod<string>(
          pipe(
            switchMap((code) => {
              store.setEmailVerificationLoading();

              return authApiService.verifyEmail(code).pipe(
                tap(() => {
                  store.setEmailVerificationLoaded();
                  alertService.showSuccess('Email verificado exitosamente');
                }),
                catchError(() => {
                  store.setEmailVerificationError(
                    'Token de verificación inválido o expirado',
                  );
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        resendVerification: rxMethod<{ email: string }>(
          pipe(
            switchMap(() => {
              store.setResendVerificationLoading();

              return authApiService.resendVerification().pipe(
                tap(() => {
                  store.setResendVerificationLoaded();
                  startCountdown();
                  alertService.showSuccess('Email de verificación enviado');
                }),
                catchError(() => {
                  store.setResendVerificationError(
                    'Error al enviar el email de verificación',
                  );
                  return EMPTY;
                }),
              );
            }),
          ),
        ),
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        effect(() => {
          const endTime = store._countdownStorage();
          const isActive = store.isCountdownActive();

          if (endTime && !isActive) {
            store._countdownStorage.set(null);
          }
        });
      },
    })),
  );
}
